import { ArrowLeft } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import homeService, { HomePageData } from "../../services/HomeService";
import { useQuery } from "@tanstack/react-query";

const LifestyleScoreDetail = () => {
  const navigate = useNavigate();

  // Use React Query to fetch and cache home page data
  const { data: homeData, isLoading: loading } = useQuery({
    queryKey: ["homePageData"],
    queryFn: () => homeService.getHomePageData(),
  });

  // Get lifestyle score from API
  const lifestyleScore = homeData?.["Lifestyle Score"] || 0;
  const maxScore = 40;
  const scorePercentage = (lifestyleScore / maxScore) * 100;

  // Get status based on score
  const getScoreStatus = (score: number) => {
    if (score >= 33) {
      return { label: "Excellent", color: "text-green-600" };
    }
    if (score >= 25) {
      return { label: "Good", color: "text-blue-600" };
    }
    if (score >= 17) {
      return { label: "Fair", color: "text-yellow-600" };
    }
    return { label: "Needs Improvement", color: "text-red-600" };
  };

  const status = getScoreStatus(lifestyleScore);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-16">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/glucera" + GluceraNavigationRoutes.HOME)}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">Lifestyle Score</h1>
          <div className="h-10 w-10" />
        </div>

        {/* Today's Stats */}
        <Card className="rounded-2xl border-0 bg-gradient-to-br from-chart-2/10 to-chart-2/5 p-6 shadow-sm">
          <div className="text-center">
            <p className="mb-2 text-sm text-muted-foreground">
              Today's Lifestyle Score
            </p>
            <div className="mb-2 text-5xl font-bold text-foreground">
              {lifestyleScore}
            </div>
            <p className="text-sm text-muted-foreground">
              of {maxScore} maximum
            </p>
            <p className={`mt-2 text-sm font-semibold ${status.color}`}>
              {status.label}
            </p>
            <div className="mt-4 h-2 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-chart-2 transition-all"
                style={{ width: `${scorePercentage}%` }}
              />
            </div>
          </div>
        </Card>

        {/* What is Lifestyle Score */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">
            What is Lifestyle Score?
          </h3>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Lifestyle Score measures your daily habits that impact overall
            health on a scale of 0-40. It includes physical activity levels,
            sleep quality, stress management, and recovery patterns. A higher
            score indicates healthier lifestyle choices that complement your
            nutrition for optimal gut health.
          </p>
          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Excellent</span>
              <span className="font-semibold">33-40</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Good</span>
              <span className="font-semibold">25-32</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Fair</span>
              <span className="font-semibold">17-24</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Needs Improvement</span>
              <span className="font-semibold">0-16</span>
            </div>
          </div>
        </Card>

        {/* Improvement Tips */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">Ways to Improve</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-chart-2">•</span>
              <span>Aim for 7-9 hours of quality sleep each night</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-chart-2">•</span>
              <span>
                Include at least 30 minutes of moderate exercise daily
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-chart-2">•</span>
              <span>
                Practice stress management through meditation or breathing
                exercises
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-chart-2">•</span>
              <span>Maintain consistent meal and sleep schedules</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default LifestyleScoreDetail;
